import type { TFunction } from 'i18next';
import { ElementFrame, FrameElementId, SlideLayout } from './slides';
import { getComponentById, getComponentLabel, isComponentId } from './slideComponents';

export type { FrameElementId };
export type LayerId = string;

export const FRAME_ELEMENT_IDS: FrameElementId[] = ['logo', 'title', 'highlights', 'image'];

export const DEFAULT_LAYER_ORDER: LayerId[] = [...FRAME_ELEMENT_IDS];

export const isBuiltinLayerId = (id: string): id is FrameElementId =>
  FRAME_ELEMENT_IDS.includes(id as FrameElementId);

/** Back-to-front order of all layers on the slide. */
export const resolveLayerOrder = (layout: SlideLayout): LayerId[] => {
  const builtIn = FRAME_ELEMENT_IDS.filter((id) => layout[id]);
  const componentIds = (layout.components ?? []).map((component) => component.id);
  const allPresent = [...builtIn, ...componentIds];
  const saved = layout.layerOrder ?? [];

  if (saved.length === 0) {
    return allPresent;
  }

  const ordered = saved.filter((id) => allPresent.includes(id));
  const missing = allPresent.filter((id) => !ordered.includes(id));
  return [...ordered, ...missing];
};

export const getLayerLabel = (
  layout: SlideLayout,
  layerId: LayerId,
  t?: TFunction,
): string => {
  if (layerId === 'background') {
    return t ? t('editor.layers.background') : 'Background';
  }
  if (isBuiltinLayerId(layerId)) {
    const labels: Record<FrameElementId, string> = {
      logo: 'Logo',
      title: 'Title',
      highlights: 'Highlights',
      image: 'Image',
    };
    return t ? t(`editor.layers.${layerId}`) : labels[layerId];
  }
  if (isComponentId(layerId)) {
    const component = getComponentById(layout, layerId);
    return component ? getComponentLabel(component, t) : t ? t('editor.layers.component') : 'Component';
  }
  return t ? t('editor.layers.layer') : 'Layer';
};

export const zIndexForLayer = (index: number, selectedBoost = 0): number =>
  10 + index + selectedBoost;

export const moveLayer = (
  order: LayerId[],
  id: LayerId,
  direction: 'forward' | 'backward' | 'front' | 'back',
): LayerId[] => {
  const index = order.indexOf(id);
  if (index === -1) {
    return order;
  }

  const next = [...order];

  if (direction === 'front') {
    next.splice(index, 1);
    next.push(id);
    return next;
  }

  if (direction === 'back') {
    next.splice(index, 1);
    next.unshift(id);
    return next;
  }

  const swapIndex = direction === 'forward' ? index + 1 : index - 1;
  if (swapIndex < 0 || swapIndex >= next.length) {
    return order;
  }

  [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
  return next;
};

export const reorderLayerOrder = (
  order: LayerId[],
  fromIndex: number,
  toIndex: number,
): LayerId[] => {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) {
    return order;
  }

  const next = [...order];
  const [moved] = next.splice(fromIndex, 1);
  if (!moved) {
    return order;
  }
  next.splice(toIndex, 0, moved);
  return next;
};

export const getLayerFrame = (layout: SlideLayout, layerId: LayerId): ElementFrame | undefined => {
  if (isBuiltinLayerId(layerId)) {
    return layout[layerId];
  }
  return getComponentById(layout, layerId)?.frame;
};
